import type { FormValues } from '@/core/components/form/formModel'
import { whoAmI } from '@/core/data/session'
import { getParticipant, updateParticipantStammdaten, type Participant, type ParticipantStammdaten } from '@/core/data/coachings'
import { getDocSnapshot } from '@/core/data/dokumente'
import { listNachweisItems, type NachweisItem } from '@/variants/azav/data/nachweise'
import { isStammdatenComplete } from '@/core/lib/stammdatenComplete'

export type ParticipantSession = {
  vorname: string
  nachname: string
}

export type PortalTabs = {
  stammdatenDone: boolean
  vertragReady: boolean
  vertragSigned: boolean


  anwesenheitslisteReady: boolean

  zertifikatReady: boolean
}

export type PortalState = {
  session: ParticipantSession
  tabs: PortalTabs
}

export type VertragView = {
  participantSigned: boolean
  pdfPath?: string
}

export type AnwesenheitslisteView = {
  pdfPath?: string
  signed: boolean
}

export type ZertifikatView = {
  pdfPath?: string
}

export type DatenschutzHeader = {
  vorname: string
  nachname: string
  geburtsdatum: string
  telefon: string
}

export type DatenschutzSelections = {
  art9: boolean
  kontaktNachMassnahme: boolean
  bewerbungsunterlagen: boolean
}

export type DatenschutzView = {
  signed: boolean

  pdfPath: string | null
  header: DatenschutzHeader

  selections: DatenschutzSelections | null
}

export function scrubGeburtsdatum(value: string | null | undefined): string {
  const v = value ?? ''
  return v === '2000-01-01' ? '' : v
}

export function teilnehmendeToFormValues(row: Participant): FormValues {
  return {
    Person: {
      Geburtsdatum: scrubGeburtsdatum(row.geburtsdatum),
    },
    Kontakt: {
      Telefon: row.phone ?? '',
      Straße: row.strasse ?? '',
      Hausnummer: row.hausnummer ?? '',
      PLZ: row.plz ?? '',
      Ort: row.ort ?? '',
    },
  }
}

export function formValuesToStammdatenPatch(values: FormValues): ParticipantStammdaten {
  const person = values.Person ?? {}
  const kontakt = values.Kontakt ?? {}
  const geburtsdatum = String(person.Geburtsdatum ?? '')

  const patch: ParticipantStammdaten = {
    telefon: String(kontakt.Telefon ?? ''),
    strasse: String(kontakt.Straße ?? ''),
    hausnummer: String(kontakt.Hausnummer ?? ''),
    plz: String(kontakt.PLZ ?? ''),
    ort: String(kontakt.Ort ?? ''),
  }
  if (geburtsdatum && geburtsdatum !== 'YYYY-MM-DD') patch.geburtsdatum = geburtsdatum
  return patch
}

export function teilnehmendeToDatenschutzHeader(row: Participant): DatenschutzHeader {
  return {
    vorname: row.vorname ?? '',
    nachname: row.nachname ?? '',
    geburtsdatum: scrubGeburtsdatum(row.geburtsdatum),
    telefon: row.phone ?? '',
  }
}

async function participantIdentity(): Promise<void> {
  const who = await whoAmI()
  if (who.kind !== 'participant') {
    throw new Error("Kein Teilnehmerprofil gefunden — bitte den Portal-Link verwenden.")
  }
}

async function ownRow(coachingId: string): Promise<Participant> {
  const row = await getParticipant(coachingId)
  if (!row) throw new Error("Kein Teilnehmerprofil gefunden — bitte den Portal-Link verwenden.")
  return row
}

export async function loadPortal(coachingId: string): Promise<{
  portal: PortalState
  vertrag: VertragView
  nachweise: NachweisItem[]
  datenschutz: DatenschutzView
  anwesenheitsliste: AnwesenheitslisteView
  zertifikat: ZertifikatView
  stammdaten: FormValues
}> {
  await participantIdentity()
  const [row, vertragDoc, anwesenheitslisteDoc, zertifikatDoc, datenschutzDoc, nachweise] = await Promise.all([
    ownRow(coachingId),
    getDocSnapshot(coachingId, 'vertrag'),
    getDocSnapshot(coachingId, 'anwesenheitsliste'),
    getDocSnapshot(coachingId, 'bescheinigung'),
    getDocSnapshot(coachingId, 'datenschutz'),
    listNachweisItems(coachingId),
  ])
  const daten = (datenschutzDoc?.daten ?? null) as { einwilligungen?: DatenschutzSelections } | null
  return {
    portal: {
      session: { vorname: row.vorname, nachname: row.nachname },
      tabs: {
        stammdatenDone: isStammdatenComplete(row),
        vertragReady: Boolean(vertragDoc?.bucketAddress),
        vertragSigned: vertragDoc?.signed ?? false,
        anwesenheitslisteReady: Boolean(anwesenheitslisteDoc?.bucketAddress),
        zertifikatReady: Boolean(zertifikatDoc?.bucketAddress),
      },
    },
    vertrag: { participantSigned: vertragDoc?.signed ?? false, pdfPath: vertragDoc?.bucketAddress || undefined },
    nachweise,
    datenschutz: {
      signed: datenschutzDoc?.signed ?? false,
      pdfPath: datenschutzDoc?.bucketAddress || null,
      header: teilnehmendeToDatenschutzHeader(row),
      selections: daten?.einwilligungen ?? null,
    },
    anwesenheitsliste: { pdfPath: anwesenheitslisteDoc?.bucketAddress || undefined, signed: anwesenheitslisteDoc?.signed ?? false },
    zertifikat: { pdfPath: zertifikatDoc?.bucketAddress || undefined },
    stammdaten: teilnehmendeToFormValues(row),
  }
}

export async function saveStammdaten(coachingId: string, values: FormValues): Promise<void> {
  await participantIdentity()
  await updateParticipantStammdaten(coachingId, formValuesToStammdatenPatch(values))
}
